// Phase 9.3 — Affiliate Tracking Dashboard (Links + Analytics + Leads + Notifications + QR)
// Phase 18 — Commissions earned + payout status surfaced to the affiliate.
import React, { useEffect, useState, useCallback } from "react";
import {
  Link as LinkIcon, Copy, QrCode, Download, BarChart3, Bell, MousePointerClick,
  TrendingUp, DollarSign, Users, RefreshCw, Loader2, Plus, Wallet,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import AffiliateProductPicker from "@/components/affiliate/AffiliateProductPicker";

const TABS = [
  { key: "links",       label: "Links",       icon: LinkIcon },
  { key: "picker",      label: "Picker",      icon: Plus },
  { key: "analytics",   label: "Products",    icon: BarChart3 },
  { key: "leads",       label: "Leads",       icon: Users },
  { key: "commissions", label: "Commissions", icon: Wallet },
  { key: "notifs",      label: "Activity",    icon: Bell },
];

function qrUrl(target, size = 220) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=10&data=${encodeURIComponent(target)}`;
}

async function downloadQr(target, filename) {
  const r = await fetch(qrUrl(target, 600));
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename + ".png"; a.click();
  URL.revokeObjectURL(url);
}

function dollars(c) { if (c == null) return "—"; return `$${(c/100).toFixed(2)}`; }

export default function AffiliateTracking() {
  const [tab, setTab] = useState("links");
  const [links, setLinks] = useState(null);
  const [track, setTrack] = useState(null);
  const [commissions, setCommissions] = useState(null);
  const [err, setErr] = useState("");
  const [busyHandle, setBusyHandle] = useState("");
  const [newHandle, setNewHandle] = useState("");
  const [qrFor, setQrFor] = useState(null);

  const load = useCallback(async () => {
    try {
      // Commissions endpoint can 404 for users without a profile — we treat
      // that as "no commissions yet" instead of a page-level error.
      const [{ data: l }, { data: t }, commRes] = await Promise.all([
        api.get("/affiliate/me/links"),
        api.get("/affiliate/me/tracking"),
        api.get("/payouts/me/dashboard").catch(() => ({ data: null })),
      ]);
      setLinks(l); setTrack(t); setCommissions(commRes.data);
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = async (v, label = "Link") => {
    try { await navigator.clipboard.writeText(v); } catch (_) {}  // eslint-disable-line no-unused-vars
  };

  const createLink = async () => {
    if (!newHandle.trim()) return;
    setBusyHandle("new");
    try {
      await api.post("/affiliate/me/links", { product_handle: newHandle.trim() });
      setNewHandle("");
      await load();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusyHandle(""); }
  };

  const markRead = async () => {
    try { await api.post("/affiliate/me/notifications/read"); await load(); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };

  if (err) return <div className="max-w-7xl mx-auto px-4 py-10 text-rose-300" data-testid="aff-tracking-error">{err}</div>;
  if (!links || !track) return <div className="max-w-7xl mx-auto px-4 py-10 text-sm text-lifted-dim">Loading…</div>;

  // Phase 3A.6 — Full absolute URLs (FE rebuilds with origin). Short link
  // REMOVED per spec Part 1. Adds explicit Main / Product / Partner / Driver /
  // Franchisee links so affiliates have one place to copy every referral URL.
  const origin = (typeof window !== "undefined" && window.location.origin) || "";
  const abs = (path) => (path?.startsWith("http") ? path : `${origin}${path || ""}`);
  const allLinks = [
    { id: "main",     label: "Main Lifted Referral Link",  url: abs(links.main_link),
      hint: "Sends visitors to the Lifted homepage. Purchases + signups under this link are credited to you." },
    { id: "partner",  label: "Partner Signup Referral Link",  url: abs(links.partner_signup_link),
      hint: "Refers a new partner store. Credits you when they sign up." },
    { id: "driver",   label: "Driver Signup Referral Link",   url: abs(links.driver_signup_link),
      hint: "Refers a new driver. Credits you when they sign up." },
    { id: "franch",   label: "Franchisee Signup Referral Link", url: abs(links.franchise_signup_link),
      hint: "Refers a new franchisee. Credits you when they sign up." },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-5" data-testid="affiliate-tracking">
      <header className="flex flex-wrap items-end gap-2 justify-between">
        <div>
          <div className="eyebrow">Affiliate · Tools</div>
          <h1 className="font-anton text-3xl uppercase">Tracking & Links</h1>
        </div>
        <button onClick={load} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="aff-refresh">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </header>

      {/* Summary KPIs */}
      <section className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="aff-kpis">
        <Kpi label="Clicks"      value={track.summary.clicks}      icon={MousePointerClick} testid="aff-kpi-clicks" />
        <Kpi label="Leads"       value={track.summary.leads}       icon={Users} testid="aff-kpi-leads" />
        <Kpi label="Conversions" value={track.summary.conversions} icon={TrendingUp} testid="aff-kpi-conv" />
        <Kpi label="Revenue"     value={dollars(track.summary.revenue_cents)} icon={DollarSign} testid="aff-kpi-revenue" />
        <Kpi label="Conv. rate"  value={`${track.summary.conversion_rate_pct}%`} icon={BarChart3} testid="aff-kpi-rate" />
        <Kpi
          label="Commission earned"
          value={dollars((commissions?.totals?.total_cents) || 0)}
          icon={Wallet}
          testid="aff-kpi-commission"
        />
      </section>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1" data-testid="aff-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-bebas tracking-[0.18em] uppercase inline-flex items-center gap-1.5 ${
                      tab === t.key ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40" : "border-white/10 text-lifted-dim hover:text-white"
                    }`}
                    data-testid={`aff-tab-${t.key}`}>
              <Icon className="h-3 w-3" /> {t.label}
              {t.key === "notifs" && track.notifications.length > 0 && (
                <span className="ml-1 bg-rose-500 text-white text-[0.55rem] rounded-full px-1.5">{track.notifications.length}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* LINKS */}
      {tab === "links" && (
        <section className="space-y-3" data-testid="aff-links-tab">
          {allLinks.map((l) => (
            <div key={l.id} className="glass-panel p-3 flex flex-wrap items-center gap-3" data-testid={`aff-link-${l.id}`}>
              <div className="flex-1 min-w-[260px]">
                <div className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">{l.label}</div>
                <div className="font-mono text-xs text-lifted-text break-all" data-testid={`aff-link-url-${l.id}`}>{l.url}</div>
                {l.hint && (
                  <div className="text-[0.65rem] text-lifted-dim mt-0.5">{l.hint}</div>
                )}
              </div>
              <button onClick={() => copy(l.url, l.label)} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid={`aff-copy-${l.id}`}>
                <Copy className="h-3 w-3" /> Copy
              </button>
              <a
                href={l.url}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost text-xs inline-flex items-center gap-1"
                data-testid={`aff-test-${l.id}`}
              >
                <LinkIcon className="h-3 w-3" /> Test
              </a>
              <button onClick={() => setQrFor(l)} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid={`aff-qr-${l.id}`}>
                <QrCode className="h-3 w-3" /> QR
              </button>
            </div>
          ))}

          {/* Product-link generator */}
          <div className="glass-panel p-4 space-y-2" data-testid="aff-product-create">
            <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-green uppercase">Create product link</div>
            <div className="flex gap-2">
              <input value={newHandle} onChange={(e) => setNewHandle(e.target.value)}
                     placeholder="product-handle (e.g. lifted-galaxy-runners-t-shirt)"
                     className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-lifted-green/40"
                     data-testid="aff-new-handle" />
              <button onClick={createLink} disabled={!newHandle.trim() || busyHandle === "new"}
                      className="btn-lifted !py-2 !px-3 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="aff-create-link">
                {busyHandle === "new" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                Generate
              </button>
            </div>
          </div>

          {/* Existing product links */}
          {links.product_links?.length > 0 && (
            <div className="glass-panel overflow-hidden" data-testid="aff-product-links">
              <div className="px-3 py-2 text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase border-b border-white/5">
                Product links · {links.product_links.length}
              </div>
              {links.product_links.map((p) => (
                <div key={p.id} className="px-3 py-2 border-b border-white/5 flex flex-wrap gap-2 items-center" data-testid={`aff-plink-${p.product_handle}`}>
                  <div className="flex-1 min-w-[220px]">
                    <div className="text-xs text-lifted-text truncate">{p.product_title}</div>
                    <div className="font-mono text-[0.65rem] text-lifted-dim truncate">{p.referral_url}</div>
                  </div>
                  <span className="text-[0.65rem] text-lifted-dim">{p.click_count || 0} clicks · {p.conversion_count || 0} conv.</span>
                  <button onClick={() => copy(p.referral_url)} className="text-lifted-dim hover:text-lifted-green" data-testid={`aff-copy-plink-${p.product_handle}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setQrFor({ id: p.id, label: p.product_title, url: p.referral_url })}
                          className="text-lifted-dim hover:text-lifted-green" data-testid={`aff-qr-plink-${p.product_handle}`}>
                    <QrCode className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* PICKER (Phase 14) */}
      {tab === "picker" && (
        <section data-testid="aff-picker-tab">
          <AffiliateProductPicker onLinkCreated={() => load()} />
        </section>
      )}

      {/* ANALYTICS */}
      {tab === "analytics" && (
        <section className="glass-panel p-4" data-testid="aff-analytics-tab">
          <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim uppercase mb-2">Hot products (by views)</div>
          {track.hot_products.length === 0 ? (
            <div className="text-xs text-lifted-dim">No product views yet — share a product link to start tracking.</div>
          ) : (
            <ul className="space-y-1.5 text-xs">
              {track.hot_products.map((p) => (
                <li key={p.product_handle} className="flex items-center justify-between" data-testid={`hot-${p.product_handle}`}>
                  <span className="text-lifted-text truncate">{p.product_handle}</span>
                  <span className="text-lifted-dim">{p.views} views</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* LEADS */}
      {tab === "leads" && (
        <section className="glass-panel overflow-hidden" data-testid="aff-leads-tab">
          {track.recent_leads.length === 0 ? (
            <div className="p-4 text-xs text-lifted-dim">No leads recorded yet.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
                <tr className="border-b border-white/5"><th className="px-3 py-2 text-left">Visitor</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Last activity</th><th className="px-3 py-2 text-left">Product</th></tr>
              </thead>
              <tbody>
                {track.recent_leads.map((l) => (
                  <tr key={l.id} className="border-b border-white/5" data-testid={`lead-${l.id}`}>
                    <td className="px-3 py-2 font-mono">{l.visitor_id?.slice(0, 12)}…</td>
                    <td className="px-3 py-2 capitalize">{(l.last_status || "clicked").replace(/_/g, " ")}</td>
                    <td className="px-3 py-2 text-[0.65rem] text-lifted-dim">{l.last_at?.slice(0, 16)?.replace("T", " ")}</td>
                    <td className="px-3 py-2 text-lifted-dim">{l.last_product || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {/* COMMISSIONS — Phase 18 */}
      {tab === "commissions" && (
        <section className="space-y-3" data-testid="aff-commissions-tab">
          {/* Status pills — totals by lifecycle */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { key: "pending_review", label: "Pending review", color: "amber" },
              { key: "approved",       label: "Approved",       color: "lifted-green" },
              { key: "paid",           label: "Paid",           color: "sky" },
              { key: "void",           label: "Voided",         color: "rose" },
            ].map((p) => (
              <div key={p.key} className="glass-panel p-4" data-testid={`aff-commission-${p.key}`}>
                <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">{p.label}</div>
                <div className="font-anton text-2xl text-white mt-1">
                  {dollars((commissions?.totals?.[p.key]) || 0)}
                </div>
              </div>
            ))}
          </div>

          {/* Commission rules — visible BEFORE creating links */}
          <div className="glass-panel p-4" data-testid="aff-commission-rules">
            <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-green uppercase mb-2">
              Commission rules
            </div>
            <ul className="text-sm text-lifted-text space-y-1.5">
              <li><span className="font-bebas tracking-wide text-lifted-green">10%</span> &nbsp;Storefront products (any sale via your link)</li>
              <li><span className="font-bebas tracking-wide text-lifted-green">50%</span> &nbsp;Affiliate program (Lift-Off subscription referrals)</li>
              <li><span className="font-bebas tracking-wide text-lifted-green">3%</span> &nbsp;Partner residual (recurring sales from partners you onboard)</li>
            </ul>
          </div>

          {/* Payout destination + status */}
          <div className="glass-panel p-4" data-testid="aff-payout-status">
            <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-green uppercase mb-2">
              Payout destination
            </div>
            {commissions?.payout_destination ? (
              <div className="text-sm">
                <div className="text-white">
                  <span className="font-bebas tracking-wide">{(commissions.payout_destination.kind || "").toUpperCase()}</span>
                  {commissions.payout_destination.display_name ? ` · ${commissions.payout_destination.display_name}` : ""}
                </div>
                <div className="text-lifted-dim text-xs mt-0.5">
                  Status: <span className={`uppercase font-bebas tracking-wide ${
                    commissions.payout_destination.status === "approved" ? "text-lifted-green" :
                    commissions.payout_destination.status === "rejected" ? "text-rose-300" : "text-amber-300"
                  }`}>{commissions.payout_destination.status || "pending"}</span>
                </div>
              </div>
            ) : (
              <div className="text-sm text-lifted-dim">
                You haven't added a payout destination yet — visit Settings → Payouts to add PayPal, Cash App or bank.
              </div>
            )}
          </div>

          {/* Recent ledger entries */}
          <div className="glass-panel overflow-hidden">
            <div className="px-3 py-2 text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase border-b border-white/5">
              Recent commissions · {commissions?.recent?.length || 0}
            </div>
            {!commissions?.recent?.length ? (
              <div className="p-4 text-xs text-lifted-dim">No activity yet — share a product link to start earning.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
                  <tr className="border-b border-white/5">
                    <th className="px-3 py-2 text-left">When</th>
                    <th className="px-3 py-2 text-left">Source</th>
                    <th className="px-3 py-2 text-left">Order</th>
                    <th className="px-3 py-2 text-right">Earned</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.recent.map((r) => (
                    <tr key={r.id} className="border-b border-white/5" data-testid={`aff-ledger-${r.id}`}>
                      <td className="px-3 py-2 text-[0.65rem] text-lifted-dim">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="px-3 py-2 capitalize">{(r.order_source || "—").replace("_", " ")}</td>
                      <td className="px-3 py-2 font-mono text-[0.65rem] truncate">{r.order_id?.slice(0, 12) || "—"}</td>
                      <td className="px-3 py-2 text-right text-lifted-green font-bebas">{dollars(r.commission_cents)}</td>
                      <td className="px-3 py-2 capitalize">{r.status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      {/* NOTIFICATIONS */}
      {tab === "notifs" && (
        <section className="glass-panel p-4 space-y-2" data-testid="aff-notifs-tab">
          <div className="flex items-center justify-between">
            <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim uppercase">Activity</div>
            {track.notifications.length > 0 && (
              <button onClick={markRead} className="text-xs text-lifted-green hover:underline" data-testid="aff-mark-read">Mark all read</button>
            )}
          </div>
          {track.notifications.length === 0 ? (
            <div className="text-xs text-lifted-dim">All caught up — no new activity.</div>
          ) : (
            <ul className="space-y-2">
              {track.notifications.map((n) => (
                <li key={n.id} className="border-l-2 border-lifted-green/40 pl-3 py-1" data-testid={`notif-${n.id}`}>
                  <div className="text-xs text-lifted-text">{n.title}</div>
                  <div className="text-[0.65rem] text-lifted-dim">{n.body}</div>
                  <div className="text-[0.55rem] text-lifted-dim mt-0.5 font-mono">{n.created_at?.slice(0, 16).replace("T", " ")}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* QR modal */}
      {qrFor && (
        <div className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4" onClick={() => setQrFor(null)} data-testid="aff-qr-modal">
          <div className="glass-panel p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-green uppercase mb-2">QR · {qrFor.label}</div>
            <img src={qrUrl(qrFor.url, 280)} alt="QR" className="mx-auto rounded-md bg-white p-2" />
            <div className="font-mono text-[0.65rem] text-lifted-dim mt-2 break-all">{qrFor.url}</div>
            <div className="mt-3 flex gap-2 justify-center">
              <button onClick={() => downloadQr(qrFor.url, `lifted-qr-${qrFor.id}`)}
                      className="btn-lifted !py-2 !px-3 text-xs inline-flex items-center gap-1" data-testid="aff-qr-download">
                <Download className="h-3 w-3" /> Download PNG
              </button>
              <button onClick={() => copy(qrFor.url)} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="aff-qr-copy-link">
                <Copy className="h-3 w-3" /> Copy link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, testid }) {
  return (
    <div className="glass-panel p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
        <Icon className="h-3.5 w-3.5 text-lifted-green" /> {label}
      </div>
      <div className="font-anton text-3xl text-white mt-1">{value}</div>
    </div>
  );
}
